import { useState, useCallback, useMemo } from 'react';

export interface PaginationParams<TSortBy extends string = string> {
  page: number;
  pageSize: number;
  sortBy?: TSortBy;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface UsePaginationOptions<TSortBy extends string = string> {
  defaultPageSize?: number;
  defaultSortBy?: TSortBy;
  defaultSortOrder?: 'asc' | 'desc';
}

export function usePagination<TSortBy extends string = string>(
  options: UsePaginationOptions<TSortBy> = {} as UsePaginationOptions<TSortBy>
) {
  const { defaultPageSize = 10, defaultSortBy, defaultSortOrder = 'asc' } = options;

  const [page, setPage] = useState(1);
  const [pageSize] = useState(defaultPageSize);
  const [sortBy, setSortBy] = useState<TSortBy | undefined>(defaultSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSort = useCallback(
    (column: string) => {
      if (sortBy === column) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(column as TSortBy);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy]
  );

  const queryParams: PaginationParams<TSortBy> = useMemo(
    () => ({ page, pageSize, sortBy, sortOrder }),
    [page, pageSize, sortBy, sortOrder]
  );

  return {
    page,
    pageSize,
    sortBy,
    sortOrder,
    queryParams,
    setPage: handlePageChange,
    handleSort
  };
}
